import { useState } from 'react';

const FORMULAS = [
  {
    title: 'Lợi nhuận gộp',
    icon: 'ri-line-chart-line',
    calc: 'Tính toán lợi nhuận',
    formula: '(Giá đóng − Giá vào) × Giá trị Pip × Khối lượng',
    desc: 'Với lệnh Buy, lợi nhuận xuất hiện khi giá tăng; với lệnh Sell, khi giá giảm. Kết quả dương là lời, âm là lỗ.',
    symbols: [
      { sym: 'Giá đóng', meaning: 'Giá tại thời điểm đóng lệnh' },
      { sym: 'Giá vào', meaning: 'Giá tại thời điểm mở lệnh' },
      { sym: 'Giá trị Pip', meaning: 'Giá trị tiền của 1 pip' },
      { sym: 'Khối lượng', meaning: 'Số lot giao dịch' },
    ],
  },
  {
    title: 'Lợi nhuận ròng',
    icon: 'ri-wallet-3-line',
    calc: 'Tính toán lợi nhuận',
    formula: 'Lợi nhuận gộp − Spread − Commission − Swap − Phí khác',
    desc: 'Đây là con số phản ánh kết quả thực tế sau khi trừ mọi chi phí giao dịch.',
    symbols: [
      { sym: 'Spread', meaning: 'Chênh lệch giá mua/bán' },
      { sym: 'Commission', meaning: 'Phí hoa hồng' },
      { sym: 'Swap', meaning: 'Phí giữ lệnh qua đêm' },
      { sym: 'Phí khác', meaning: 'Các loại phí phát sinh' },
    ],
  },
  {
    title: 'Chi phí Spread',
    icon: 'ri-percent-line',
    calc: 'Tính chi phí Spread',
    formula: 'Spread × Giá trị Pip × Khối lượng',
    desc: 'Spread là chênh lệch giữa giá mua và giá bán. Chi phí này được trừ trực tiếp vào kết quả mỗi giao dịch.',
    symbols: [
      { sym: 'Spread', meaning: 'Chênh lệch giá mua/bán (pip)' },
      { sym: 'Giá trị Pip', meaning: 'Giá trị tiền của 1 pip' },
      { sym: 'Khối lượng', meaning: 'Số lot giao dịch' },
    ],
  },
  {
    title: 'Lợi nhuận kép',
    icon: 'ri-repeat-line',
    calc: 'Máy tính lợi nhuận kép',
    formula: 'Vốn × (1 + r)^n + Khoản nạp thêm × ((1 + r)^n − 1) / r',
    desc: 'Trong đó r là tỷ suất mỗi kỳ, n là số kỳ. Phép tính dựa trên vốn ban đầu, tỷ suất và khoản nạp thêm định kỳ.',
    symbols: [
      { sym: 'Vốn', meaning: 'Số vốn ban đầu' },
      { sym: 'r', meaning: 'Tỷ suất lợi nhuận mỗi kỳ' },
      { sym: 'n', meaning: 'Số kỳ (tuần/tháng)' },
      { sym: 'Khoản nạp thêm', meaning: 'Tiền nạp thêm định kỳ' },
    ],
  },
];

export default function FormulaSection() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
          Công thức
        </span>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
          Công thức nền tảng cho cả ba công cụ
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">
          Ba công cụ phía trên — tính lợi nhuận, chi phí Spread và lợi nhuận kép — đều vận hành trên những phép tính cụ thể.
          Mở từng mục bên dưới để xem công thức và cách nó hoạt động.
        </p>
      </div>

      <div className="mt-6 space-y-3">
        {FORMULAS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.title} className="overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50/50">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                className="flex w-full items-center gap-3 px-5 py-4 text-left"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
                  <i className={`${f.icon}`} />
                </span>
                <span className="flex-1 text-sm font-bold text-slate-800">{f.title}</span>
                <span className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700 ring-1 ring-violet-100 sm:inline-flex">
                  <i className="ri-corner-right-up-line text-xs" />
                  {f.calc}
                </span>
                <i className={`ri-arrow-down-s-line text-lg text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="border-t border-slate-200/70 px-5 py-4">
                  <p className="font-mono text-sm font-semibold text-violet-700">{f.formula}</p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">{f.desc}</p>

                  {f.symbols && f.symbols.length > 0 && (
                    <div className="mt-4 border-t border-slate-200/70 pt-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Ký hiệu</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {f.symbols.map((s) => (
                          <span
                            key={s.sym}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[11px] ring-1 ring-slate-200/80"
                          >
                            <span className="font-mono font-semibold text-violet-600">{s.sym}</span>
                            <span className="text-slate-500">{s.meaning}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}